import { motion } from 'framer-motion';
import { EASE_EXPO } from '@/lib/motion';
import { SectionShell } from './SectionShell';

/**
 * Roadmap — five phases as a vertical timeline.
 *
 * Verbatim from project_investor_deck.md. Each phase is a stage in the
 * platform's expansion, drawn with a continuous brand-coloured spine on
 * desktop to communicate sequence.
 */

const PHASES: { num: string; title: string; body: string }[] = [
  {
    num: '01',
    title: 'AI driven workflow solution plus transaction processing',
    body: 'Run the operational case spine, AI assisted drafting, approvals, audit trail, document vault, receipts, payments and reporting in one platform',
  },
  {
    num: '02',
    title: 'AI agent depth',
    body: 'Train governed agents on approved historical case patterns so recommendations improve over time without removing practitioner control',
  },
  {
    num: '03',
    title: 'Global expansion',
    body: 'A flick of a switch unlocks other jurisdictions whose insolvency legislation is closely modelled on UK law',
  },
  {
    num: '04',
    title: 'Asset classes + valuation/recoverability intelligence',
    body: 'Introduce four asset classes with AI valuation, recoverability signals and investigation support',
  },
  {
    num: '05',
    title: 'Full asset search stack',
    body: 'Broaden coverage and automation depth across asset discovery and tracing',
  },
];

export function Roadmap() {
  return (
    <SectionShell
      id="roadmap"
      index={6}
      eyebrow="Roadmap"
      title={
        <>
          Phased expansion across{' '}
          <span className="text-brand-bright">five releases</span>
        </>
      }
      intro="Step 1 establishes the AI driven operating system. Steps 2 through 5 deepen the agent layer, unlock jurisdiction packs, and expand into valuation, recoverability and full asset search"
    >
      <ol className="relative flex flex-col gap-6">
        {/* Spine on the left edge — runs the full height of the timeline.
            It draws downward as the timeline enters view, so the five
            releases read as a sequence being laid out rather than a list
            that was always there. */}
        <motion.div
          aria-hidden
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.5, ease: EASE_EXPO }}
          style={{ transformOrigin: 'top' }}
          className="pointer-events-none absolute bottom-2 left-[15px] top-2 hidden w-px bg-gradient-to-b from-brand-bright/60 via-border-strong to-transparent sm:block"
        />
        {PHASES.map((phase, i) => (
          <PhaseRow key={phase.num} phase={phase} index={i} />
        ))}
      </ol>
    </SectionShell>
  );
}

function PhaseRow({
  phase,
  index,
}: {
  phase: (typeof PHASES)[number];
  index: number;
}) {
  return (
    <motion.li
      initial={{ opacity: 0, x: -16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="relative flex items-start gap-5 sm:gap-8"
    >
      {/* Node — sits on the spine. */}
      <div className="relative z-10 mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-full border border-accent-blue-border bg-bg-page text-[10px] font-semibold uppercase tabular-nums tracking-[0.18em] text-brand-bright">
        {phase.num}
      </div>

      <div className="pitch-card pitch-card-interactive flex flex-1 flex-col gap-2 rounded-2xl p-6 sm:p-7">
        <h3 className="text-balance text-[20px] font-semibold leading-snug text-ink-strong sm:text-[22px]">
          {phase.title}
        </h3>
        <p className="text-[14px] leading-relaxed text-ink-muted">
          {phase.body}
        </p>
      </div>
    </motion.li>
  );
}
